import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { identityAPI, authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from '../components/shared/index';
import toast from 'react-hot-toast';

const ID_TYPES = [
  { value: 'aadhaar',         label: 'Aadhaar Card' },
  { value: 'college_id',      label: 'College ID Card' },
  { value: 'passport',        label: 'Passport' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'voter_id',        label: 'Voter ID' },
];

export default function IdentityVerificationPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // OTP state
  const [otpSent, setOtpSent]       = useState(false);
  const [otpValue, setOtpValue]     = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [cooldown, setCooldown]     = useState(0); // seconds remaining

  // Form state
  const [form, setForm] = useState({
    govIdType: 'aadhaar', govIdNumber: '',
    govIdFront: null, govIdBack: null, selfie: null
  });
  const [previews, setPreviews] = useState({});

  // ── Cooldown timer ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    identityAPI.getStatus()
      .then(({ data }) => setStatus(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleFileChange = (field) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be under 5MB.'); return; }
    setForm(f => ({ ...f, [field]: file }));
    setPreviews(p => ({ ...p, [field]: URL.createObjectURL(file) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.govIdFront) { toast.error('Government ID front image is required.'); return; }
    if (!form.govIdNumber.trim()) { toast.error('ID number is required.'); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('govIdType',   form.govIdType);
      fd.append('govIdNumber', form.govIdNumber);
      fd.append('govIdFront',  form.govIdFront);
      if (form.govIdBack) fd.append('govIdBack', form.govIdBack);
      if (form.selfie)    fd.append('selfie',    form.selfie);

      await identityAPI.submit(fd);
      toast.success('Documents submitted! Admin will review within 24 hours.');
      await refreshUser();
      const updated = await identityAPI.getStatus();
      setStatus(updated.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendOtp = async () => {
    setSendingOtp(true);
    try {
      const { data } = await authAPI.sendPhoneOtp();
      setOtpSent(true);
      setCooldown(60); // 60-second resend cooldown
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpValue || otpValue.length !== 6) { toast.error('Please enter the 6-digit code.'); return; }
    setVerifyingOtp(true);
    try {
      await authAPI.verifyPhone({ otp: otpValue });
      toast.success('Phone number verified! ✅');
      setOtpValue('');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Incorrect code. Please try again.');
      setOtpValue('');
    } finally {
      setVerifyingOtp(false);
    }
  };

  if (loading) return <Spinner size="lg" centered />;

  const currentStatus = status?.identityStatus || user?.identityStatus || 'not_submitted';

  return (
    <div className="container section fade-in" style={{ maxWidth: 760 }}>
      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <p className="eyebrow">Account Security</p>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 44, fontStyle: 'italic' }}>Identity Verification</h1>
        <p className="text-muted text-sm" style={{ marginTop: 8 }}>
          A one-time process to keep our community safe. Your documents are encrypted and stored securely.
        </p>
      </div>

      {/* Steps progress */}
      <div style={styles.steps}>
        <Step num={1} label="Email Verified"  done={user?.isEmailVerified} />
        <div style={styles.stepLine} />
        <Step num={2} label="Phone Verified"  done={user?.isPhoneVerified} />
        <div style={styles.stepLine} />
        <Step num={3} label="ID Verified"     done={currentStatus === 'approved'} pending={currentStatus === 'pending'} />
      </div>

      {/* ── Step 1: Email ─────────────────────────────────────────────────── */}
      {!user?.isEmailVerified && (
        <div style={styles.warningBox}>
          <span style={{ fontSize: 22 }}>📧</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Email Not Verified</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Check your inbox for the verification link. The link expires in 24 hours.
            </div>
            <button
              className="btn btn-outline btn-sm"
              style={{ marginTop: 10 }}
              onClick={async () => {
                try {
                  await authAPI.resendVerification({ email: user.email });
                  toast.success('Verification email resent!');
                } catch { toast.error('Failed. Try again in a moment.'); }
              }}
            >
              Resend Verification Email
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Phone OTP ─────────────────────────────────────────────── */}
      {user?.isEmailVerified && !user?.isPhoneVerified && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📱 Verify Phone Number</h3>

          {/* SMS OTP temporarily unavailable */}
          <div style={{ background: '#fff8e1', border: '1px solid #ffc107', padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#856404', lineHeight: 1.7 }}>
            <strong>📢 Temporarily Unavailable</strong><br />
            SMS verification is currently offline for maintenance. You can skip this step and continue to submit your identity documents. Phone verification will be re-enabled soon.
          </div>

          {!user?.phone ? (
            <p className="text-muted text-sm">
              Please <a href="/profile" style={{ color: 'var(--gold)' }}>add a phone number</a> to your profile first.
            </p>
          ) : (
            <>
              <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                We'll send a 6-digit code to <strong>+91 ••••••{user.phone.slice(-4)}</strong>
              </p>

              {!otpSent ? (
                <button className="btn btn-primary" onClick={sendOtp} disabled={sendingOtp}>
                  {sendingOtp ? 'Sending...' : 'Send Verification Code'}
                </button>
              ) : (
                <div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Enter 6-Digit Code</label>
                      <input
                        className="input"
                        value={otpValue}
                        onChange={e => setOtpValue(e.target.value.replace(/\D/, '').slice(0, 6))}
                        placeholder="• • • • • •"
                        maxLength={6}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        style={{ width: 160, letterSpacing: '0.3em', textAlign: 'center', fontSize: 20 }}
                      />
                    </div>
                    <button className="btn btn-primary" onClick={verifyOtp} disabled={verifyingOtp || otpValue.length !== 6}>
                      {verifyingOtp ? 'Verifying...' : 'Verify'}
                    </button>
                  </div>

                  <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>
                    Didn't receive it?{' '}
                    {cooldown > 0 ? (
                      <span style={{ color: 'var(--muted)' }}>Resend in {cooldown}s</span>
                    ) : (
                      <button
                        className="btn-link"
                        style={{ color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', padding: 0 }}
                        onClick={sendOtp}
                        disabled={sendingOtp}
                      >
                        Resend code
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Step 3: Identity Documents ────────────────────────────────────── */}
      {user?.isEmailVerified && (
        <>
          {currentStatus === 'approved' && (
            <div style={{ ...styles.statusBanner, background: '#e8f5e9', border: '1px solid #4caf50' }}>
              <span style={{ fontSize: 28 }}>✅</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#1b5e20' }}>Identity Verified</div>
                <div style={{ fontSize: 12, color: '#2e7d32', marginTop: 2 }}>You can now place rental orders.</div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/browse')}>Browse Collection →</button>
            </div>
          )}

          {currentStatus === 'pending' && (
            <div style={{ ...styles.statusBanner, background: '#fff8e1', border: '1px solid #ffc107' }}>
              <span style={{ fontSize: 28 }}>⏳</span>
              <div>
                <div style={{ fontWeight: 700, color: '#856404' }}>Under Review</div>
                <div style={{ fontSize: 12, color: '#856404', marginTop: 2 }}>Your documents are being reviewed. We'll notify you within 24 hours.</div>
              </div>
            </div>
          )}

          {currentStatus === 'rejected' && (
            <div style={{ ...styles.statusBanner, background: '#ffebee', border: '1px solid #f44336' }}>
              <span style={{ fontSize: 28 }}>❌</span>
              <div>
                <div style={{ fontWeight: 700, color: '#b71c1c' }}>Verification Rejected</div>
                <div style={{ fontSize: 12, color: '#c62828', marginTop: 2 }}>
                  {status?.verification?.rejectedReason || 'Please resubmit with clearer documents.'}
                </div>
              </div>
            </div>
          )}

          {(currentStatus === 'not_submitted' || currentStatus === 'rejected') && (
            <form onSubmit={handleSubmit} style={styles.section}>
              <h3 style={styles.sectionTitle}>🪪 Upload Identity Documents</h3>
              <p className="text-muted text-sm" style={{ marginBottom: 24 }}>
                All documents are encrypted and reviewed only by authorized admin staff.
              </p>

              <div className="grid-2 gap-16" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">ID Type *</label>
                  <select className="input" value={form.govIdType} onChange={e => setForm(f => ({ ...f, govIdType: e.target.value }))}>
                    {ID_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">ID Number *</label>
                  <input
                    className="input"
                    value={form.govIdNumber}
                    onChange={e => setForm(f => ({ ...f, govIdNumber: e.target.value }))}
                    placeholder={form.govIdType === 'aadhaar' ? '1234 5678 9012' : 'Enter ID number'}
                    required
                  />
                </div>
              </div>

              <div className="grid-3 gap-16">
                <FileBox label="ID Front *" sublabel="Required" preview={previews.govIdFront}  onChange={handleFileChange('govIdFront')}  required />
                <FileBox label="ID Back"    sublabel="Optional"  preview={previews.govIdBack}   onChange={handleFileChange('govIdBack')} />
                <FileBox label="Selfie"     sublabel="Recommended" preview={previews.selfie}   onChange={handleFileChange('selfie')} icon="🤳" />
              </div>

              <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', padding: '10px 14px', margin: '16px 0', fontSize: 11, color: 'var(--muted)', lineHeight: 2 }}>
                🔒 AES-256 encrypted · Compliant with DPDP Act 2023 · Deleted after 90 days of account closure
              </div>

              <button type="submit" className="btn btn-primary btn-lg" disabled={submitting || !form.govIdFront}>
                {submitting ? <><Spinner size="sm" /> Uploading...</> : 'Submit for Verification'}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}

function Step({ num, label, done, pending }) {
  const bg = done ? '#4caf50' : pending ? '#ffc107' : 'var(--border)';
  const color = done || pending ? 'white' : 'var(--muted)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
        {done ? '✓' : num}
      </div>
      <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: done ? '#2e7d32' : 'var(--muted)', fontWeight: 600 }}>{label}</span>
    </div>
  );
}

function FileBox({ label, sublabel, preview, onChange, required, icon = '🪪' }) {
  return (
    <div>
      <div className="form-label">{label}</div>
      <label style={styles.fileBox}>
        {preview ? (
          <img src={preview} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 8 }}>
            <div style={{ fontSize: 28, marginBottom: 4 }}>{icon}</div>
            <div style={{ fontSize: 10 }}>{sublabel}</div>
            <div style={{ fontSize: 9, marginTop: 4, opacity: 0.7 }}>Click to upload · JPG/PNG · Max 5MB</div>
          </div>
        )}
        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onChange} required={required} style={{ display: 'none' }} />
      </label>
    </div>
  );
}

const styles = {
  steps:        { display: 'flex', alignItems: 'center', marginBottom: 32, padding: '20px 24px', background: 'white', border: '1px solid var(--border)' },
  stepLine:     { flex: 1, height: 2, background: 'var(--border)', margin: '0 12px' },
  section:      { background: 'white', border: '1px solid var(--border)', padding: 28, marginBottom: 20 },
  sectionTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 24, marginBottom: 14 },
  warningBox:   { display: 'flex', gap: 16, alignItems: 'flex-start', background: '#fff8e1', border: '1px solid #ffc107', padding: 20, marginBottom: 20 },
  statusBanner: { display: 'flex', gap: 16, alignItems: 'center', padding: 20, marginBottom: 20 },
  fileBox:      { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 140, border: '2px dashed var(--border)', cursor: 'pointer', overflow: 'hidden', background: 'var(--cream)', transition: 'border-color 0.2s' },
};